import { useEffect, useState } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";

const CHAPTERS = [
  { id: "top", n: "00", label: "Enter" },
  { id: "network", n: "01", label: "Transit" },
  { id: "products", n: "02", label: "Catalog" },
  { id: "why", n: "03", label: "Manifesto" },
  { id: "industries", n: "04", label: "Industries" },
  { id: "partners", n: "05", label: "Channels" },
  { id: "contact", n: "06", label: "Desk" },
];

// Vertical rail on the right edge that (1) tracks the reader's chapter
// (2) continuously ships glowing "data packets" downward like a factory
// conveyor. Reinforces the "travelling through the network" feel.
export default function DataRail() {
  const [active, setActive] = useState("top");
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 22,
    mass: 0.4,
  });
  const scaleY = useTransform(progress, [0, 1], [0, 1]);

  useEffect(() => {
    const ids = CHAPTERS.map((c) => c.id);
    const els = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        // Choose the entry closest to top that is visible
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) =>
              Math.abs(a.boundingClientRect.top) -
              Math.abs(b.boundingClientRect.top)
          );
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: [0, 0.2, 0.5] }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div
      className="hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 z-40 flex-col items-end gap-4 pointer-events-none"
      aria-hidden="true"
      data-testid="data-rail"
    >
      {/* Rail track */}
      <div className="relative h-[440px] w-[2px] mr-[68px]">
        <div className="absolute inset-0 bg-white/8" />
        <motion.div
          style={{ scaleY, transformOrigin: "top" }}
          className="absolute inset-0 bg-gradient-to-b from-electric-500 via-electric-500/70 to-transparent"
        />

        {/* Traveling packets */}
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            initial={{ y: 0, opacity: 0 }}
            animate={{ y: 440, opacity: [0, 1, 1, 0] }}
            transition={{
              duration: 3.4,
              delay: i * 1.1,
              repeat: Infinity,
              ease: "linear",
            }}
            className="absolute left-1/2 -translate-x-1/2 w-[6px] h-[6px] rounded-full bg-electric-500"
            style={{ boxShadow: "0 0 12px #00E5FF" }}
          />
        ))}

        {/* Chapter ticks */}
        {CHAPTERS.map((c, i) => {
          const top = (i / (CHAPTERS.length - 1)) * 100;
          const isActive = active === c.id;
          return (
            <a
              key={c.id}
              href={`#${c.id}`}
              style={{ top: `${top}%` }}
              className="pointer-events-auto absolute -left-[66px] flex items-center gap-3 -translate-y-1/2 whitespace-nowrap group"
              data-testid={`rail-chapter-${c.id}`}
            >
              <span
                className={`font-mono text-[10px] tracking-[0.22em] uppercase transition-colors duration-300 ${
                  isActive ? "text-electric-500" : "text-slate-500 group-hover:text-slate-200"
                }`}
              >
                {c.n} · {c.label}
              </span>
              <span
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  isActive
                    ? "bg-electric-500 scale-125"
                    : "bg-black/25 group-hover:bg-black/60"
                }`}
                style={
                  isActive
                    ? { boxShadow: "0 0 10px #00E5FF" }
                    : undefined
                }
              />
            </a>
          );
        })}
      </div>
    </div>
  );
}

